/**
 * =============================================================================
 * IDS CRM Web - Reactive Ticket Create/Edit Form
 * =============================================================================
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import BackToList from '../components/BackToList';
import { useAuth } from '../context/AuthContext';
import EngineerSelect from '../components/EngineerSelect';
import CustomerSearchSelect from '../components/CustomerSearchSelect';
import { useUnsavedChanges } from '../hooks/useUnsavedChanges';
import { filesToAttachmentPayload, savedAttachmentsOnly } from '../utils/attachments';
import { formatDateKey, toDateKey } from '../utils/dates';
import './TicketForm.css';

function toLocalDateTime(value = new Date()) {
  const date = value instanceof Date ? value : new Date(value);
  const offsetMs = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
}

// Pre-guess for the fast-path Work Period: evenings/weekends default to
// out-of-hours (it drives OOH call pay), but the field stays visible for
// the engineer to correct.
function guessWorkPeriod() {
  const now = new Date();
  const day = now.getDay();
  const hour = now.getHours();
  return (day === 0 || day === 6 || hour < 8 || hour >= 18) ? 'out_of_hours' : 'in_hours';
}

function TicketForm() {
  const { guardElement, markDirty } = useUnsavedChanges();
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { authFetch, user } = useAuth();
  // RR5: commercial raise a service request - intake fields only, no
  // call-log fast path (that creates a work log) and no assignment.
  const isCommercialIntake = user?.role === 'commercial';
  const isEdit = Boolean(id);
  const initialCustomerId = searchParams.get('customer_id') || searchParams.get('customer') || '';
  const initialAssetId = searchParams.get('asset_id') || searchParams.get('asset') || '';

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [attachmentError, setAttachmentError] = useState('');
  const [staleRecord, setStaleRecord] = useState(false);
  const [assets, setAssets] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [engineers, setEngineers] = useState([]);
  const [customerLabel, setCustomerLabel] = useState('');
  const [distributorLabel, setDistributorLabel] = useState('');
  const [duplicateTickets, setDuplicateTickets] = useState([]);
  // Fault linking (2026-08-11): recently closed tickets on the asset are
  // offered as "link as follow-on"; previousTicket is the chosen link.
  const [recentClosed, setRecentClosed] = useState([]);
  // Open work anywhere on the customer — context, not a duplicate warning.
  const [customerOpenTickets, setCustomerOpenTickets] = useState([]);
  const [previousTicket, setPreviousTicket] = useState(null);
  const followOnSourceId = searchParams.get('follow_on') || '';

  const currentUserId = user?.userId || '';

  const [formData, setFormData] = useState({
    source_type: 'direct',
    distributor_id: '',
    customer_id: '',
    contact_id: '',
    asset_id: '',
    use_non_listed_asset: false,
    non_listed_asset_label: '',
    subject: '',
    customer_reported_fault: '',
    engineer_fault_interpretation: '',
    channel: 'phone',
    priority: 'normal',
    support_tier: 'first_line',
    fault_category: '',
    assigned_to_id: currentUserId,
    reported_start_at: toLocalDateTime(),
    attachments: []
  });

  // "Call handled — log it now" (ticket streamline 2026-08-01): create the
  // ticket and the call's work log from one form. A closing outcome closes
  // the ticket; a handover outcome logs the call but keeps it open — either
  // way the completed work log exists, which is what OOH call pay reads.
  const [callLog, setCallLog] = useState({
    enabled: false,
    outcome: '',
    work_period: guessWorkPeriod(),
    ended_at: toLocalDateTime(),
    narrative: '',
    assign_next_type: '',
    assigned_to_next_id: ''
  });
  const [workLogConfig, setWorkLogConfig] = useState({ work_periods: [], outcomes: [] });
  // Set when the ticket saved but the call log didn't - resubmitting would
  // duplicate the ticket, so the form locks and links to the ticket instead.
  const [createdTicketId, setCreatedTicketId] = useState(null);

  const selectedCallOutcome = workLogConfig.outcomes?.find(o => o.value === callLog.outcome);
  const callOutcomeCloses = Boolean(selectedCallOutcome?.closes_work);

  useEffect(() => {
    if (currentUserId && !isEdit) {
      setFormData(prev => ({ ...prev, assigned_to_id: prev.assigned_to_id || currentUserId }));
    }
  }, [currentUserId, isEdit]);

  const [supportConfig, setSupportConfig] = useState({
    comms_channels: [
      { value: 'phone', label: 'Phone' },
      { value: 'whatsapp', label: 'WhatsApp' },
      { value: 'email', label: 'Email' },
      { value: 'portal', label: 'Portal' }
    ],
    support_tiers: [
      { value: 'first_line', label: 'First Line' },
      { value: 'second_line', label: 'Second Line' }
    ]
  });

  const priorities = [
    { value: 'low', label: 'Low' },
    { value: 'normal', label: 'Normal' },
    { value: 'high', label: 'High' },
    { value: 'critical', label: 'Critical' }
  ];

  const [faultCategories, setFaultCategories] = useState([{ value: '', label: 'Select...' }]);
  const selectedAsset = assets.find(asset => asset.asset_id === formData.asset_id);
  const selectedAssetType = selectedAsset?.robot_type || '';
  // L8: advisory availability warnings for the assigned engineer (today)
  const [availabilityWarnings, setAvailabilityWarnings] = useState([]);
  useEffect(() => {
    const checkAvailability = async () => {
      if (!formData.assigned_to_id) {
        setAvailabilityWarnings([]);
        return;
      }
      try {
        const response = await authFetch(`/api/v1/engineers/${formData.assigned_to_id}/availability`);
        if (response.ok) {
          const data = await response.json();
          setAvailabilityWarnings(data.warnings || []);
        } else {
          setAvailabilityWarnings([]);
        }
      } catch (err) {
        setAvailabilityWarnings([]);
      }
    };
    checkAvailability();
  }, [authFetch, formData.assigned_to_id]);

  // R5b (DL-28): three-tier cover — warranty, then the asset's snapshotted
  // service/repair-&-replace policy (withheld if the account is on stop),
  // else billable. Mirrors the server-side ticket billing default.
  // The viewer's own day: toISOString() is the UTC day, a day behind from
  // 00:00 to 00:59 BST and a day ahead in the evening west of UTC.
  const coverTodayStr = toDateKey(new Date());
  const assetInWarranty = Boolean(
    selectedAsset?.warranty_expiry_date
    && String(selectedAsset.warranty_expiry_date).slice(0, 10) >= coverTodayStr
  );
  const assetInPolicy = Boolean(
    !assetInWarranty
    && selectedAsset?.cover_policy
    && selectedAsset?.cover_policy_end_date
    && String(selectedAsset.cover_policy_end_date).slice(0, 10) >= coverTodayStr
  );
  const coverWithheld = assetInPolicy && selectedAsset?.account_on_stop === true;

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const [configRes, engineersRes, workLogConfigRes] = await Promise.all([
          authFetch('/api/v1/support/config-lists'),
          authFetch('/api/v1/tickets/active-engineers'),
          authFetch('/api/v1/support/work-log-config')
        ]);

        if (configRes.ok) {
          const data = await configRes.json();
          setSupportConfig(prev => ({
            ...prev,
            ...(data.data || {})
          }));
        }

        if (workLogConfigRes.ok) {
          const data = await workLogConfigRes.json();
          const config = data.data || data || {};
          setWorkLogConfig({
            work_periods: config.work_periods || [],
            outcomes: config.outcomes || []
          });
          // Keep the pre-guessed period only if the configured list has it
          setCallLog(prev => (
            (config.work_periods || []).some(p => p.value === prev.work_period)
              ? prev
              : { ...prev, work_period: config.work_periods?.[0]?.value || prev.work_period }
          ));
        }

        if (engineersRes.ok) {
          const data = await engineersRes.json();
          const rows = data.data || [];
          setEngineers(rows);
        }
      } catch (err) {
        console.error('Failed to fetch ticket config:', err);
      }
    };

    fetchConfig();
  }, [authFetch]);

  useEffect(() => {
    const fetchFaultCategories = async () => {
      try {
        const suffix = selectedAssetType ? `?asset_type=${encodeURIComponent(selectedAssetType)}` : '';
        const faultRes = await authFetch(`/api/v1/support/fault-categories${suffix}`);
        if (faultRes.ok) {
          const data = await faultRes.json();
          const rows = [
            { value: '', label: 'Select...' },
            ...(data.data || []).map(c => ({ value: c.value, label: c.label }))
          ];
          setFaultCategories(rows);
          setFormData(prev => (
            prev.fault_category && !rows.some(row => row.value === prev.fault_category)
              ? { ...prev, fault_category: '' }
              : prev
          ));
        }
      } catch (err) {
        console.error('Failed to fetch fault categories:', err);
      }
    };

    fetchFaultCategories();
  }, [authFetch, selectedAssetType]);

  // Fires on the CUSTOMER alone (Gareth 2026-09-10) — an engineer needs to see
  // ongoing work at the farm before picking an asset, and often the relevant
  // ticket is on a different robot or has no asset at all. The asset, once
  // chosen, adds the duplicate warning and the fault-link offers on top.
  useEffect(() => {
    if (isEdit || !formData.customer_id) {
      setDuplicateTickets([]);
      setRecentClosed([]);
      setCustomerOpenTickets([]);
      return undefined;
    }

    const assetScoped = formData.asset_id && !formData.use_non_listed_asset;

    const checkDuplicates = async () => {
      try {
        const params = new URLSearchParams({ customer_id: formData.customer_id });
        if (assetScoped) params.set('asset_id', formData.asset_id);
        const response = await authFetch(`/api/v1/tickets/check-duplicate?${params}`);
        const data = await response.json();
        setDuplicateTickets(data.data || []);
        setRecentClosed(data.recent_closed || []);
        setCustomerOpenTickets(data.customer_open || []);
      } catch (err) {
        console.error('Open ticket check failed:', err);
      }
    };

    const debounce = setTimeout(checkDuplicates, 300);
    return () => clearTimeout(debounce);
  }, [isEdit, formData.customer_id, formData.asset_id, formData.use_non_listed_asset, authFetch]);

  // A chosen follow-on link only makes sense for its own customer.
  useEffect(() => {
    if (previousTicket && previousTicket.customer_id && previousTicket.customer_id !== formData.customer_id) {
      setPreviousTicket(null);
    }
  }, [formData.customer_id, previousTicket]);

  // "Raise Follow-on" from a closed ticket: prefill from the source ticket
  // and pre-set the link.
  useEffect(() => {
    if (isEdit || !followOnSourceId) return;
    const loadFollowOnSource = async () => {
      try {
        const response = await authFetch(`/api/v1/tickets/${followOnSourceId}`);
        if (!response.ok) return;
        const source = await response.json();
        setPreviousTicket({
          ticket_id: source.ticket_id,
          ticket_ref: source.ticket_ref,
          subject: source.subject,
          customer_id: source.customer_id
        });
        setCustomerLabel(source.customer_name || '');
        setFormData(prev => ({
          ...prev,
          source_type: source.source_type || 'direct',
          distributor_id: source.distributor_id || '',
          customer_id: source.customer_id || '',
          contact_id: source.contact_id || '',
          asset_id: source.asset_id || '',
          use_non_listed_asset: Boolean(source.non_listed_asset_label),
          non_listed_asset_label: source.non_listed_asset_label || '',
          subject: `Recurring: ${source.subject}`,
          fault_category: source.fault_category || ''
        }));
      } catch (err) {
        console.error('Failed to load follow-on source ticket:', err);
      }
    };
    loadFollowOnSource();
  }, [isEdit, followOnSourceId, authFetch]);

  useEffect(() => {
    if (!formData.customer_id) {
      setAssets([]);
      setContacts([]);
      return;
    }

    const fetchRelated = async () => {
      try {
        const [assetsRes, contactsRes] = await Promise.all([
          authFetch(`/api/v1/assets?customer_id=${formData.customer_id}&limit=100`),
          authFetch(`/api/v1/contacts?customer_id=${formData.customer_id}&limit=50`)
        ]);
        const assetsData = await assetsRes.json();
        const contactsData = await contactsRes.json();
        // Assets awaiting install aren't in service yet - no tickets against them
        setAssets((assetsData.data || []).filter(a => a.status !== 'pending_install'));
        setContacts(contactsData.data || []);
      } catch (err) {
        console.error('Failed to fetch related data:', err);
      }
    };

    fetchRelated();
  }, [formData.customer_id, authFetch]);

  useEffect(() => {
    if (!isEdit) return;

    const fetchTicket = async () => {
      try {
        const response = await authFetch(`/api/v1/tickets/${id}`);
        if (!response.ok) {
          setError('Ticket not found');
          return;
        }
        const data = await response.json();
        setFormData({
          source_type: data.source_type || 'direct',
          distributor_id: data.distributor_id || '',
          customer_id: data.customer_id || '',
          contact_id: data.contact_id || '',
          asset_id: data.asset_id || '',
          use_non_listed_asset: Boolean(data.non_listed_asset_label),
          non_listed_asset_label: data.non_listed_asset_label || '',
          subject: data.subject || '',
          customer_reported_fault: data.customer_reported_fault || data.description || '',
          engineer_fault_interpretation: data.engineer_fault_interpretation || '',
          channel: data.channel || 'phone',
          priority: data.priority || 'normal',
          support_tier: data.support_tier || 'first_line',
          fault_category: data.fault_category || '',
          assigned_to_id: data.assigned_to_id || currentUserId,
          reported_start_at: toLocalDateTime(data.reported_start_at || data.opened_at || new Date()),
          attachments: data.attachments || [],
          updated_at: data.updated_at || ''
        });
        setCustomerLabel(data.customer_name || '');
        setDistributorLabel(data.distributor_name || '');
      } catch (err) {
        setError('Failed to load ticket');
      } finally {
        setLoading(false);
      }
    };

    fetchTicket();
  }, [id, isEdit, authFetch, currentUserId]);

  useEffect(() => {
    if (isEdit || (!initialCustomerId && !initialAssetId)) return;

    const fetchInitialContext = async () => {
      try {
        if (initialAssetId) {
          const assetRes = await authFetch(`/api/v1/assets/${initialAssetId}`);
          if (!assetRes.ok) return;
          const asset = await assetRes.json();
          setFormData(prev => ({
            ...prev,
            customer_id: asset.customer_id || prev.customer_id,
            asset_id: asset.asset_id
          }));
          setCustomerLabel(asset.customer_name || '');
          return;
        }

        const customerRes = await authFetch(`/api/v1/customers/${initialCustomerId}`);
        if (!customerRes.ok) return;
        const customer = await customerRes.json();
        setFormData(prev => ({ ...prev, customer_id: customer.customer_id }));
        setCustomerLabel(customer.business_name || '');
      } catch (err) {
        console.error('Failed to load initial ticket context:', err);
      }
    };

    fetchInitialContext();
  }, [authFetch, initialAssetId, initialCustomerId, isEdit]);

  const handleChange = async (e) => {
    const { name, value, type, checked, files } = e.target;
    if (type === 'file') {
      const picker = e.target;
      setAttachmentError('');
      try {
        const attachments = await filesToAttachmentPayload(files);
        setFormData(prev => ({ ...prev, attachments }));
      } catch (err) {
        // Refused files are not attached. The picker is emptied so it cannot go on
        // showing files that will not be saved with the ticket, and the reason is
        // shown beside it: the banner at the top of this form is off screen.
        picker.value = '';
        setFormData(prev => ({ ...prev, attachments: savedAttachmentsOnly(prev.attachments) }));
        setAttachmentError(err.message);
      }
      return;
    }

    if (name === 'source_type' && value === 'direct') setDistributorLabel('');
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
      ...(name === 'source_type' && value === 'direct' ? { distributor_id: '' } : {}),
      ...(name === 'use_non_listed_asset' && checked ? { asset_id: '' } : {}),
      ...(name === 'asset_id' && value ? { use_non_listed_asset: false, non_listed_asset_label: '' } : {})
    }));
  };

  const handleCustomerSelect = (row) => {
    markDirty();
    setFormData(prev => ({
      ...prev,
      customer_id: row ? row.customer_id : '',
      contact_id: '',
      asset_id: '',
      use_non_listed_asset: false,
      non_listed_asset_label: ''
    }));
    setCustomerLabel(row ? row.business_name : '');
  };

  const handleDistributorSelect = (row) => {
    markDirty();
    // A different distributor means a different customer pool - drop any
    // customer already picked so a mismatched pairing can't be saved.
    setFormData(prev => ({
      ...prev,
      distributor_id: row ? row.customer_id : '',
      customer_id: '',
      contact_id: '',
      asset_id: '',
      use_non_listed_asset: false,
      non_listed_asset_label: ''
    }));
    setDistributorLabel(row ? row.business_name : '');
    setCustomerLabel('');
  };

  // Melissa (2026-08-01): the title box confused the team - reports run on
  // fault category, so the title auto-fills when left blank.
  const autoSubject = () => {
    const faultLabel = formData.fault_category
      ? faultCategories.find(f => f.value === formData.fault_category)?.label
      : null;
    const assetLabel = formData.use_non_listed_asset
      ? formData.non_listed_asset_label.trim()
      : (selectedAsset?.asset_ref || '');
    if (faultLabel) return assetLabel ? `${faultLabel} — ${assetLabel}` : faultLabel;
    return formData.customer_reported_fault.trim().slice(0, 80);
  };

  const validate = () => {
    if (formData.source_type === 'distributor' && !formData.distributor_id) return 'Please select a distributor';
    if (!formData.customer_id) return 'Please select a customer';
    if (!formData.customer_reported_fault.trim()) return 'Please enter the customer reported fault';
    if (!formData.asset_id && !formData.non_listed_asset_label.trim()) return 'Select an asset or choose Non-listed Asset';
    if (!isEdit && callLog.enabled) {
      if (!callLog.outcome) return 'Pick an outcome for the handled call (or untick "Call handled")';
      if (!callLog.work_period) return 'Pick the work period for the call';
      if (!callLog.ended_at) return 'Enter when the call ended';
      if (callOutcomeCloses && !callLog.narrative.trim()) return 'Resolution summary is required when the outcome closes the ticket';
      if (!callOutcomeCloses && !callLog.assign_next_type) return 'Choose who takes the follow-up (the outcome keeps the ticket open)';
      if (callLog.assign_next_type === 'user' && !callLog.assigned_to_next_id) return 'Pick the engineer taking the follow-up';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      setSaving(false);
      return;
    }

    try {
      const url = isEdit ? `/api/v1/tickets/${id}` : '/api/v1/tickets';
      const method = isEdit ? 'PUT' : 'POST';
      const description = [
        formData.customer_reported_fault,
        formData.engineer_fault_interpretation ? `Engineer interpretation: ${formData.engineer_fault_interpretation}` : ''
      ].filter(Boolean).join('\n\n');

      const payload = {
        source_type: formData.source_type,
        distributor_id: formData.source_type === 'distributor' ? formData.distributor_id : null,
        customer_id: formData.customer_id,
        contact_id: formData.contact_id || null,
        asset_id: formData.use_non_listed_asset ? null : (formData.asset_id || null),
        non_listed_asset_label: formData.use_non_listed_asset ? formData.non_listed_asset_label.trim() : null,
        subject: formData.subject.trim() || autoSubject(),
        description,
        customer_reported_fault: formData.customer_reported_fault.trim(),
        engineer_fault_interpretation: formData.engineer_fault_interpretation || null,
        channel: formData.channel,
        priority: formData.priority,
        support_tier: formData.support_tier,
        fault_category: formData.fault_category || null,
        assigned_to_id: isCommercialIntake ? null : (formData.assigned_to_id || null),
        reported_start_at: formData.reported_start_at ? new Date(formData.reported_start_at).toISOString() : null,
        attachments: formData.attachments,
        is_warranty: false,
        is_billable: false
      };

      if (!isEdit && previousTicket) {
        payload.previous_ticket_id = previousTicket.ticket_id;
      }

      // Optimistic lock: send back the updated_at we loaded so a concurrent
      // edit by someone else returns 409 instead of being silently overwritten
      if (isEdit && formData.updated_at) {
        payload.updated_at = formData.updated_at;
      }

      const response = await authFetch(url, {
        method,
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();

        // Fast path: log the handled call against the ticket we just made.
        // If this second call fails the ticket still exists (today's normal
        // state after step one) - lock the form so a resubmit can't create
        // a duplicate, and point at the ticket to finish the log there.
        if (!isEdit && callLog.enabled) {
          const logResponse = await authFetch(`/api/v1/tickets/${data.ticket_id}/work-log`, {
            method: 'POST',
            body: JSON.stringify({
              log_type: 'remote_session',
              started_at: new Date(formData.reported_start_at).toISOString(),
              ended_at: new Date(callLog.ended_at).toISOString(),
              work_period: callLog.work_period,
              outcome: callLog.outcome,
              notes: callOutcomeCloses ? null : callLog.narrative.trim(),
              resolution_summary: callOutcomeCloses ? callLog.narrative.trim() : null,
              assign_next_type: callOutcomeCloses ? null : callLog.assign_next_type,
              assigned_to_next_id: !callOutcomeCloses && callLog.assign_next_type === 'user'
                ? callLog.assigned_to_next_id
                : null
            })
          });
          if (!logResponse.ok) {
            const logError = await logResponse.json().catch(() => ({}));
            setCreatedTicketId(data.ticket_id);
            setError(`Ticket ${data.ticket_ref || ''} was created, but logging the call failed: ${logError.message || 'unknown error'}. Open the ticket and use Log Work to record the call.`);
            setSaving(false);
            return;
          }
        }

        navigate(`/tickets/${data.ticket_id}`);
      } else {
        const errorData = await response.json();
        if (response.status === 409) setStaleRecord(true);
        setError(errorData.message || 'Failed to save ticket');
      }
    } catch (err) {
      setError('Failed to save ticket');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="ticket-form-loading">Loading...</div>;

  return (
    <div className="ticket-form-page">
      <div className="ticket-form-header">
        <BackToList to="/tickets" className="back-link">&larr; Jobs &amp; Tickets</BackToList>
        <h1>{isEdit ? 'Edit Reactive Ticket' : 'New Reactive Ticket'}</h1>
      </div>

      {guardElement}
      <form onChangeCapture={markDirty} onSubmit={handleSubmit} className="ticket-form">
        {error && (
          <div className="ticket-form-error">
            <span>{error}</span>
            {staleRecord && (
              <button type="button" className="form-error-link" onClick={() => window.location.reload()}>
                Reload latest version
              </button>
            )}
          </div>
        )}

        <div className="ticket-form-section">
          <h2>Customer Route</h2>
          <div className="ticket-form-grid">
            <div className="ticket-form-group">
              <label htmlFor="source_type">Direct or Distributor *</label>
              <select id="source_type" name="source_type" value={formData.source_type} onChange={handleChange}>
                <option value="direct">Direct</option>
                <option value="distributor">Distributor</option>
              </select>
            </div>

            {formData.source_type === 'distributor' && (
              <div className="ticket-form-group full-width">
                <label htmlFor="distributor_search">Distributor *</label>
                <CustomerSearchSelect
                  id="distributor_search"
                  kind="distributor"
                  value={formData.distributor_id}
                  initialLabel={distributorLabel}
                  placeholder="Search distributors..."
                  onSelect={handleDistributorSelect}
                />
              </div>
            )}

            <div className="ticket-form-group full-width">
              <label htmlFor="customer_search">Customer *</label>
              <CustomerSearchSelect
                id="customer_search"
                kind="customer"
                distributorId={formData.source_type === 'distributor' ? formData.distributor_id || null : null}
                value={formData.customer_id}
                initialLabel={customerLabel}
                onSelect={handleCustomerSelect}
              />
            </div>
          </div>
        </div>

        <div className="ticket-form-section">
          <h2>Asset &amp; Contact</h2>
          <div className="ticket-form-grid">
            {contacts.length > 0 && (
              <div className="ticket-form-group">
                <label htmlFor="contact_id">Contact</label>
                <select id="contact_id" name="contact_id" value={formData.contact_id} onChange={handleChange}>
                  <option value="">Optional...</option>
                  {contacts.map(c => (
                    <option key={c.contact_id} value={c.contact_id}>{c.full_name} {c.is_primary && '(Primary)'}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="ticket-form-group">
              <label htmlFor="asset_id">Asset *</label>
              <select
                id="asset_id"
                name="asset_id"
                value={formData.asset_id}
                onChange={handleChange}
                disabled={!formData.customer_id || formData.use_non_listed_asset}
              >
                <option value="">Select asset...</option>
                {assets.map(a => (
                  <option key={a.asset_id} value={a.asset_id}>
                    {a.asset_ref} - {String(a.robot_type || '').replace(/_/g, ' ')} {a.serial_number ? `(${a.serial_number})` : ''}
                  </option>
                ))}
              </select>
            </div>

            {assetInWarranty && !isEdit && (
              <div className="warranty-banner">
                This asset is under warranty until {formatDateKey(selectedAsset.warranty_expiry_date)} —
                the ticket will default to <strong>warranty (non-billable)</strong>.
              </div>
            )}

            {assetInPolicy && !coverWithheld && !isEdit && (
              <div className="warranty-banner">
                Covered by the {selectedAsset.cover_policy?.designation === 'service' ? 'Service'
                  : selectedAsset.cover_policy?.designation === 'repair_replace' ? 'Repair & Replace'
                  : 'service / repair & replace'} agreement until {formatDateKey(selectedAsset.cover_policy_end_date)} —
                the ticket will default to <strong>covered (non-billable)</strong>.
              </div>
            )}

            {coverWithheld && !isEdit && (
              <div className="duplicate-ticket-warning">
                <strong>Cover withheld — account on stop.</strong> This asset's policy would cover the
                work, but the customer's account is not up to date; the ticket will default
                to <strong>billable</strong> until the account stop is lifted.
              </div>
            )}

            {/* Ongoing work at this customer, shown as soon as the customer is
                picked. Deliberately NOT styled as a warning: nothing is wrong,
                the engineer just needs to know what else is live before adding
                to it. The asset-specific duplicate warning below is the one
                that says "you may be about to raise the same thing twice". */}
            {!isEdit && customerOpenTickets.length > 0 && (
              <div className="customer-open-tickets">
                <span className="customer-open-tickets-heading">
                  {customerOpenTickets.length === 1
                    ? 'This customer has 1 other open ticket:'
                    : `This customer has ${customerOpenTickets.length} other open tickets:`}
                </span>
                <ul>
                  {customerOpenTickets.map(t => (
                    <li key={t.ticket_id}>
                      <Link to={`/tickets/${t.ticket_id}`} target="_blank" rel="noreferrer">
                        {t.ticket_ref}
                      </Link>
                      {' — '}{t.subject}
                      <span className="customer-open-tickets-meta">
                        {t.site_name ? ` · ${t.site_name}` : ''}
                        {t.asset_ref ? ` · ${t.asset_ref}` : ''}
                        {` · ${String(t.status).replace(/_/g, ' ')}`}
                        {t.assigned_to_name ? ` · ${t.assigned_to_name}` : ' · unassigned'}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {duplicateTickets.length > 0 && (
              <div className="duplicate-ticket-warning">
                <span>
                  {duplicateTickets.length === 1
                    ? 'This asset already has an open ticket:'
                    : `This asset already has ${duplicateTickets.length} open tickets:`}
                </span>
                <ul>
                  {duplicateTickets.map(t => (
                    <li key={t.ticket_id}>
                      <Link to={`/tickets/${t.ticket_id}`} target="_blank" rel="noreferrer">
                        {t.ticket_ref}
                      </Link> — {t.subject} ({t.status.replace(/_/g, ' ')})
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Fault linking: chosen follow-on link, or the offer list */}
            {!isEdit && previousTicket && (
              <div className="followon-linked-banner">
                <span>
                  Will be linked as a follow-on of <strong>{previousTicket.ticket_ref}</strong>
                  {previousTicket.subject ? ` — ${previousTicket.subject}` : ''}
                </span>
                <button type="button" className="followon-unlink-btn" onClick={() => setPreviousTicket(null)}>
                  Remove link
                </button>
              </div>
            )}
            {!isEdit && !previousTicket && recentClosed.length > 0 && (
              <div className="followon-offer">
                <span>
                  Recently closed on this asset — is this the same fault back again?
                </span>
                <ul>
                  {recentClosed.map(t => (
                    <li key={t.ticket_id}>
                      <Link to={`/tickets/${t.ticket_id}`} target="_blank" rel="noreferrer">
                        {t.ticket_ref}
                      </Link> — {t.subject}
                      {t.closed_at ? ` (closed ${new Date(t.closed_at).toLocaleDateString('en-GB')})` : ''}
                      <button
                        type="button"
                        className="followon-link-btn"
                        onClick={() => setPreviousTicket({
                          ticket_id: t.ticket_id,
                          ticket_ref: t.ticket_ref,
                          subject: t.subject,
                          customer_id: formData.customer_id
                        })}
                      >
                        Link as follow-on
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="ticket-form-group ticket-checkbox-group">
              <label className="ticket-checkbox-label">
                <input
                  type="checkbox"
                  name="use_non_listed_asset"
                  checked={formData.use_non_listed_asset}
                  onChange={handleChange}
                />
                Non-listed Asset
              </label>
            </div>

            {formData.use_non_listed_asset && (
              <div className="ticket-form-group">
                <label htmlFor="non_listed_asset_label">Non-listed asset description *</label>
                <input
                  id="non_listed_asset_label"
                  name="non_listed_asset_label"
                  value={formData.non_listed_asset_label}
                  onChange={handleChange}
                  placeholder="e.g. Brush robot not yet recorded"
                />
              </div>
            )}
          </div>
        </div>

        <div className="ticket-form-section">
          <h2>Fault Details</h2>
          <div className="ticket-form-grid">
            <div className="ticket-form-group full-width">
              <label htmlFor="customer_reported_fault">Customer reported fault *</label>
              <textarea id="customer_reported_fault" name="customer_reported_fault" value={formData.customer_reported_fault} onChange={handleChange} rows={4} />
            </div>

            {!callLog.enabled && (
              <div className="ticket-form-group full-width">
                <label htmlFor="engineer_fault_interpretation">Engineer interpretation / handover notes</label>
                <textarea id="engineer_fault_interpretation" name="engineer_fault_interpretation" value={formData.engineer_fault_interpretation} onChange={handleChange} rows={3} />
              </div>
            )}

            <div className="ticket-form-group full-width">
              <label htmlFor="subject">Ticket title (auto-filled if left blank)</label>
              <input
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                placeholder={autoSubject() ? `Auto: ${autoSubject()}` : 'Auto-filled from fault category and asset'}
              />
            </div>

            <div className="ticket-form-group">
              <label htmlFor="channel">Comms channel *</label>
              <select id="channel" name="channel" value={formData.channel} onChange={handleChange} required>
                {(supportConfig.comms_channels || []).map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>

            <div className="ticket-form-group">
              <label htmlFor="fault_category">Fault category</label>
              <select id="fault_category" name="fault_category" value={formData.fault_category} onChange={handleChange}>
                {faultCategories.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
              </select>
            </div>
          </div>
        </div>

        <div className="ticket-form-section">
          <h2>Priority &amp; Assignment</h2>
          <div className="ticket-form-grid">
            <div className="ticket-form-group">
              <label htmlFor="priority">Priority *</label>
              <select id="priority" name="priority" value={formData.priority} onChange={handleChange} required>
                {priorities.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>

            <div className="ticket-form-group">
              <label htmlFor="support_tier">Support tier</label>
              <select id="support_tier" name="support_tier" value={formData.support_tier} onChange={handleChange}>
                {(supportConfig.support_tiers || []).map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>

            {!isCommercialIntake && (
            <div className="ticket-form-group">
              <label htmlFor="assigned_to_id">Assigned engineer</label>
              <EngineerSelect
                id="assigned_to_id"
                name="assigned_to_id"
                value={formData.assigned_to_id}
                onChange={handleChange}
                placeholder="Unassigned"
                valueKey="user_id"
                engineers={currentUserId && !engineers.some(e => e.user_id === currentUserId)
                  ? [{ user_id: currentUserId, full_name: user?.full_name || user?.email || 'Me' }, ...engineers]
                  : engineers}
              />
              {availabilityWarnings.length > 0 && (
                <div className="availability-warnings">
                  {availabilityWarnings.map((warning, index) => (
                    <span key={index} className="availability-warning-chip">⚠ {warning}</span>
                  ))}
                </div>
              )}
            </div>
            )}

            <div className="ticket-form-group">
              <label htmlFor="reported_start_at">Start date/time *</label>
              <input id="reported_start_at" type="datetime-local" name="reported_start_at" value={formData.reported_start_at} onChange={handleChange} required />
            </div>
          </div>
        </div>

        <div className="ticket-form-section">
          <h2>Attachments</h2>
          <div className="ticket-form-grid">
            <div className="ticket-form-group full-width">
              <label htmlFor="attachments">Attachments</label>
              <input id="attachments" type="file" multiple onChange={handleChange} />
              {attachmentError && <div className="ticket-form-error" role="alert">{attachmentError}</div>}
              {formData.attachments.length > 0 && (
                <div className="ticket-search-status">{formData.attachments.length} attachment filename(s) captured</div>
              )}
            </div>
          </div>
        </div>

        {!isEdit && !isCommercialIntake && (
          <div className="ticket-form-section call-log-section">
            <label className="call-log-toggle">
              <input
                type="checkbox"
                checked={callLog.enabled}
                onChange={(e) => setCallLog(prev => ({ ...prev, enabled: e.target.checked }))}
              />
              <span>
                <strong>Call handled — log it now</strong>
                <small>Records the call as a work log with the ticket. A closing outcome closes the ticket; a handover outcome keeps it open for follow-up. Either way the call counts for time and on-call pay.</small>
              </span>
            </label>

            {callLog.enabled && (
              <div className="ticket-form-grid">
                <div className="ticket-form-group">
                  <label htmlFor="call_outcome">Outcome *</label>
                  <select
                    id="call_outcome"
                    value={callLog.outcome}
                    onChange={(e) => setCallLog(prev => ({ ...prev, outcome: e.target.value }))}
                    required
                  >
                    <option value="">Select...</option>
                    <optgroup label="Closes the ticket">
                      {(workLogConfig.outcomes || []).filter(o => o.closes_work).map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </optgroup>
                    <optgroup label="Keeps it open (hands over)">
                      {(workLogConfig.outcomes || []).filter(o => !o.closes_work).map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </optgroup>
                  </select>
                </div>

                <div className="ticket-form-group">
                  <label htmlFor="call_work_period">Work period *</label>
                  <select
                    id="call_work_period"
                    value={callLog.work_period}
                    onChange={(e) => setCallLog(prev => ({ ...prev, work_period: e.target.value }))}
                    required
                  >
                    {(workLogConfig.work_periods || []).map(p => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </select>
                </div>

                <div className="ticket-form-group">
                  <label htmlFor="call_ended_at">Call ended *</label>
                  <input
                    id="call_ended_at"
                    type="datetime-local"
                    value={callLog.ended_at}
                    onChange={(e) => setCallLog(prev => ({ ...prev, ended_at: e.target.value }))}
                    required
                  />
                </div>

                {!callOutcomeCloses && callLog.outcome && (
                  <div className="ticket-form-group">
                    <label htmlFor="call_assign_next">Follow-up goes to *</label>
                    <select
                      id="call_assign_next"
                      value={callLog.assign_next_type}
                      onChange={(e) => setCallLog(prev => ({ ...prev, assign_next_type: e.target.value }))}
                      required
                    >
                      <option value="">Select...</option>
                      <option value="self">Me</option>
                      <option value="user">Another engineer</option>
                      <option value="group">Maintenance Planning</option>
                    </select>
                  </div>
                )}

                {!callOutcomeCloses && callLog.assign_next_type === 'user' && (
                  <div className="ticket-form-group">
                    <label htmlFor="call_assigned_next">Engineer *</label>
                    <EngineerSelect
                      id="call_assigned_next"
                      name="call_assigned_next"
                      value={callLog.assigned_to_next_id}
                      onChange={(e) => setCallLog(prev => ({ ...prev, assigned_to_next_id: e.target.value }))}
                      placeholder="Select engineer"
                      valueKey="user_id"
                      engineers={engineers}
                    />
                  </div>
                )}

                <div className="ticket-form-group full-width">
                  <label htmlFor="call_narrative">
                    {callOutcomeCloses || !callLog.outcome ? 'Resolution summary' : 'Call notes / handover notes'}
                    {callOutcomeCloses ? ' *' : ''}
                  </label>
                  <textarea
                    id="call_narrative"
                    value={callLog.narrative}
                    onChange={(e) => setCallLog(prev => ({ ...prev, narrative: e.target.value }))}
                    rows={3}
                    placeholder={callOutcomeCloses || !callLog.outcome ? 'What fixed the customer issue?' : 'What was done on the call, and what the follow-up needs'}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        <div className="ticket-form-actions">
          <BackToList to="/tickets" className="ticket-btn-cancel">Cancel</BackToList>
          {createdTicketId ? (
            <Link to={`/tickets/${createdTicketId}`} className="ticket-btn-save">
              Open the created ticket
            </Link>
          ) : (
            <button type="submit" className="ticket-btn-save" disabled={saving}>
              {saving ? 'Saving...' : (isEdit ? 'Save Changes' : (callLog.enabled ? 'Create Ticket & Log Call' : 'Create Reactive Ticket'))}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

export default TicketForm;
